/**
 * Shared types, constants, and dedup logic for unified action items.
 *
 * Used by both the Action Items page (alerts/index.tsx) and
 * the domain Enforcement tab (EnforcementTab.tsx).
 */

import type { Alert, AICachedInsight, RemediationTask, SigningDomain } from "@/lib/api";

// ============================================================================
// Unified Action Item — normalized shape for insights, tasks, and alerts
// ============================================================================

export interface UnifiedItem {
  id: string;
  source: "insight" | "task" | "alert";
  priority: "critical" | "high" | "medium" | "low" | "info";
  title: string;
  description: string;
  recommendations: string[];
  category: string;
  isCompleted: boolean;
  createdAt: string;
  actionLink?: string;
  confidence?: "high" | "medium" | "low";
  metadata?: Record<string, unknown>;
  // Original data for actions
  rawAlert?: Alert;
  rawTask?: RemediationTask;
  rawInsight?: AICachedInsight;
}

// ============================================================================
// Constants
// ============================================================================

export const TASK_TO_INSIGHT_MAP: Record<string, string[]> = {
  update_spf: ["dns_action_needed"],
  add_dkim: ["dkim_action_needed"],
  configure_dkim: ["dkim_action_needed"],
  investigate_source: ["spoofing_detected"],
  update_dmarc_policy: ["spoofing_detected"],
  fix_dns_record: ["authentication_issue"],
};

export const PRIORITY_ORDER: Record<string, number> = {
  critical: 0,
  high: 1,
  medium: 2,
  low: 3,
  info: 4,
};

export const BORDER_COLORS: Record<string, string> = {
  critical: "border-l-red-500",
  high: "border-l-orange-500",
  medium: "border-l-amber-400",
  low: "border-l-blue-400",
  info: "border-l-slate-400",
};

export const SEVERITY_BADGE: Record<string, string> = {
  critical: "bg-red-100 text-red-700",
  high: "bg-orange-100 text-orange-700",
  medium: "bg-amber-100 text-amber-700",
  low: "bg-blue-100 text-blue-700",
  info: "bg-slate-100 text-slate-600",
};

export const TYPE_LABELS: Record<string, string> = {
  dns_action_needed: "DNS Change",
  dkim_action_needed: "DKIM Setup",
  provider_issue: "Provider Issue",
  authentication_issue: "Auth Issue",
  spoofing_detected: "Spoofing",
  volume_anomaly: "Volume Change",
  forwarder_impact: "Forwarder",
  signing_domain_misalignment: "Signing Domain",
};

export const SOURCE_BADGE: Record<string, { label: string; className: string }> = {
  insight: { label: "AI Insight", className: "bg-purple-100 text-purple-700" },
  task: { label: "Task", className: "bg-blue-100 text-blue-700" },
  alert: { label: "Alert", className: "bg-amber-100 text-amber-700" },
};

// ============================================================================
// Dedup helpers
// ============================================================================

export function normalizeProvider(name: string): string {
  return name
    .toLowerCase()
    .replace(/\s+(inc\.?|llc\.?|corp\.?|ltd\.?|north america|international)$/i, "")
    .replace(/\s+/g, " ")
    .trim();
}

export function extractProviderFromTitle(title: string): string | null {
  // "Add Microsoft 365 to pacificdebt.com SPF record (3 sources)" -> "Microsoft 365"
  const addMatch = title.match(/^Add (.+?) to (?:\S+\s)?(?:SPF|DKIM|DMARC)/i);
  if (addMatch) return normalizeProvider(addMatch[1]);
  // "Configure DKIM signing for SendGrid" -> "SendGrid"
  const configMatch = title.match(/^Configure (?:DKIM|SPF|DMARC).*? for (.+?)(?:\s*\(\d+\s*sources?\))?$/i);
  if (configMatch) return normalizeProvider(configMatch[1]);
  // "Configure Mimecast DKIM for pacificdebt.com" -> "Mimecast"
  const configProviderMatch = title.match(/^Configure (.+?) (?:DKIM|SPF|DMARC)/i);
  if (configProviderMatch) return normalizeProvider(configProviderMatch[1]);
  // "Google Workspace has authentication issues" -> "Google Workspace"
  const authMatch = title.match(/^(.+?) has authentication issues$/i);
  if (authMatch) return normalizeProvider(authMatch[1]);
  return null;
}

export function getInsightProvider(insight: AICachedInsight): string {
  return (insight.metadata?.providerName as string)
    || (insight.metadata?.provider as string)
    || extractProviderFromTitle(insight.title)
    || "";
}

export function buildInsightKeys(insights: AICachedInsight[]): Set<string> {
  const keys = new Set<string>();
  for (const insight of insights) {
    const providerName = getInsightProvider(insight);
    if (providerName) {
      keys.add(`${normalizeProvider(providerName)}|${insight.type}`);
    }
  }
  return keys;
}

export function isTaskDuplicate(task: RemediationTask, insightKeys: Set<string>): boolean {
  const provider = extractProviderFromTitle(task.title);
  if (!provider) return false;

  const matchingInsightTypes = TASK_TO_INSIGHT_MAP[task.task_type] || [];
  for (const insightType of matchingInsightTypes) {
    if (insightKeys.has(`${provider}|${insightType}`)) return true;
  }
  return false;
}

export function isAlertDuplicate(alert: Alert, insightKeys: Set<string>): boolean {
  const provider = extractProviderFromTitle(alert.title);
  if (provider) {
    for (const key of insightKeys) {
      if (key.startsWith(provider + "|")) return true;
    }
  }

  const match = alert.title.match(/New sending source detected:\s*(.+)/i);
  if (match) {
    const sourceProvider = normalizeProvider(match[1]);
    for (const key of insightKeys) {
      if (key.startsWith(sourceProvider + "|")) return true;
    }
  }

  return false;
}

// ============================================================================
// Task action link computation
// ============================================================================

export function computeTaskActionLink(task: RemediationTask): string | undefined {
  const { task_type, domain_id, source_id } = task;

  switch (task_type) {
    case "update_spf":
    case "fix_dns_record":
    case "add_dkim":
    case "configure_dkim":
      return domain_id ? `/domains/${domain_id}?tab=configuration` : undefined;
    case "update_dmarc_policy":
      return domain_id ? `/domains/${domain_id}?tab=enforcement` : undefined;
    default:
      if (source_id) return `/sources?highlight=${source_id}`;
      return undefined;
  }
}

// ============================================================================
// Build unified feed from insights, tasks, and alerts
// ============================================================================

export function buildUnifiedFeed(
  insights: AICachedInsight[],
  tasks: RemediationTask[],
  alerts: Alert[],
): UnifiedItem[] {
  const items: UnifiedItem[] = [];
  const insightKeys = buildInsightKeys(insights);

  // 1. Insights (always shown, highest quality) — dedup same provider+type
  const seenInsightKeys = new Set<string>();
  for (const insight of insights) {
    if (insight.type === "account_digest") continue;
    const insightProvider = getInsightProvider(insight);
    if (insightProvider) {
      const dedupKey = `${normalizeProvider(insightProvider)}|${insight.type}|${insight.domainId || ""}`;
      if (seenInsightKeys.has(dedupKey)) continue;
      seenInsightKeys.add(dedupKey);
    }
    items.push({
      id: `insight-${insight.id}`,
      source: "insight",
      priority: insight.severity,
      title: insight.title,
      description: insight.description,
      recommendations: insight.recommendations,
      category: TYPE_LABELS[insight.type] || insight.type.replace(/_/g, " "),
      isCompleted: false,
      createdAt: insight.createdAt,
      actionLink: insight.actionLink || undefined,
      confidence: (insight.confidence as "high" | "medium" | "low") || undefined,
      metadata: insight.metadata || undefined,
      rawInsight: insight,
    });
  }

  // 2. Tasks — only if not duplicated by an insight
  const seenTaskKeys = new Set<string>();
  for (const task of tasks) {
    if (isTaskDuplicate(task, insightKeys)) continue;

    const taskKey = `${task.title}|${task.domain_id}|${task.status}`;
    if (seenTaskKeys.has(taskKey)) continue;
    seenTaskKeys.add(taskKey);

    items.push({
      id: `task-${task.id}`,
      source: "task",
      priority: task.priority,
      title: task.title,
      description: task.description,
      recommendations: task.suggested_record ? [`Suggested: ${task.suggested_record}`] : [],
      category: task.task_type?.replace(/_/g, " ") || "Task",
      isCompleted: task.status === "completed" || task.status === "skipped",
      createdAt: task.created_at,
      actionLink: computeTaskActionLink(task),
      rawTask: task,
    });
  }

  // 3. Alerts — only if not duplicated by an insight
  for (const alert of alerts) {
    if (isAlertDuplicate(alert, insightKeys)) continue;

    items.push({
      id: `alert-${alert.id}`,
      source: "alert",
      priority: alert.severity === "warning" ? "medium" : alert.severity === "critical" ? "critical" : "low",
      title: alert.title,
      description: alert.message || alert.description,
      recommendations: [],
      category: alert.alert_type.replace(/_/g, " "),
      isCompleted: alert.status === "resolved",
      createdAt: alert.created_at,
      rawAlert: alert,
    });
  }

  return items;
}

// ============================================================================
// Build signing domain misalignment items
// ============================================================================

export function buildSigningDomainItems(
  signingDomains: SigningDomain[],
  domainId: string,
  domainName: string,
): UnifiedItem[] {
  const misaligned = signingDomains.filter((sd) => !sd.aligned);
  if (misaligned.length === 0) return [];

  const items: UnifiedItem[] = [];

  // Summary item
  items.push({
    id: `signing-domain-summary-${domainId}`,
    source: "alert",
    priority: "high",
    title: `${misaligned.length} external domain${misaligned.length !== 1 ? "s" : ""} signing as ${domainName}`,
    description: `${misaligned.length} DKIM signing domain${misaligned.length !== 1 ? "s are" : " is"} misaligned — third parties are signing email with Header From: ${domainName} using their own DKIM domains.`,
    recommendations: [
      "Review each misaligned signing domain to determine if it's authorized",
      "Contact partners and request they configure DKIM to sign with your domain",
      "Create an APEX share link to share details with the partner",
    ],
    category: TYPE_LABELS.signing_domain_misalignment,
    isCompleted: false,
    createdAt: new Date().toISOString(),
    actionLink: `/sources?domain=${domainId}&view=signing_domains`,
  });

  // Per-domain items for most impactful misaligned domains (top 5)
  for (const sd of misaligned.slice(0, 5)) {
    const providerNames = sd.providers.map((p) => p.provider_name).join(", ");
    items.push({
      id: `signing-domain-${domainId}-${sd.dkim_domain}`,
      source: "alert",
      priority: sd.total_messages > 100 ? "high" : "medium",
      title: `${sd.dkim_domain} is signing as ${domainName}`,
      description: `${sd.total_messages.toLocaleString()} messages signed by ${sd.dkim_domain} with Header From: ${domainName}. DKIM pass rate: ${sd.pass_rate}%.${providerNames ? ` Associated providers: ${providerNames}.` : ""}`,
      recommendations: [
        "Share an APEX link with this partner to coordinate DKIM alignment",
        "Ask the partner to configure DKIM signing with your domain",
      ],
      category: TYPE_LABELS.signing_domain_misalignment,
      isCompleted: false,
      createdAt: new Date().toISOString(),
      actionLink: `/sources?domain=${domainId}&view=signing_domains`,
    });
  }

  return items;
}
